import { useCallback, useEffect, type ReactNode } from 'react';
import { Button, useToast } from 'pink-lava-ui';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError, isCancel } from 'axios';
import type { ApiResponse } from '@dc-trans/data-layer';
import { useNotification } from './notificationContext';
import { CopyIcon } from 'lucide-react';

const DEFAULT_ERROR_TITLE = 'Permintaan Gagal';
const DEFAULT_ERROR_DESCRIPTION = 'Terjadi kesalahan pada sistem. Silakan coba lagi.';

export interface ApiErrorContext {
  error: unknown;
  variables?: unknown;
}

type MetaValue<T> = T | ((_context: ApiErrorContext) => T);

export interface ApiErrorNotificationMeta extends Record<string, unknown> {
  skip?: MetaValue<boolean>;
  message?: MetaValue<string>;
  description?: MetaValue<ReactNode>;
  hideTraceIdAction?: MetaValue<boolean>;
}

const resolveMetaValue = <T,>(value: MetaValue<T> | undefined, context: ApiErrorContext): T | undefined => {
  if (typeof value === 'function') {
    const fn = value as (_context: ApiErrorContext) => T;
    return fn(context);
  }
  return value;
};

const extractDescription = (error: unknown): ReactNode => {
  if (isAxiosError<ApiResponse<unknown>>(error)) {
    const apiMessage = error.response?.data?.message;
    if (apiMessage) return apiMessage;
    if (error.message) return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return DEFAULT_ERROR_DESCRIPTION;
};

const extractTraceId = (error: unknown): string | undefined => {
  if (!isAxiosError<ApiResponse<unknown>>(error)) return undefined;
  const traceHeader =
    error.response?.headers?.['x-trace-id'] ??
    error.response?.headers?.['X-Trace-Id'] ??
    error.response?.headers?.['X-Trace-ID'];
  if (!traceHeader) return undefined;

  return Array.isArray(traceHeader) ? traceHeader[0] : String(traceHeader);
};

const ApiErrorNotificationProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [showToast, toastHolder] = useToast();

  const emitErrorNotification = useCallback(
    (error: unknown, meta: ApiErrorNotificationMeta | undefined, variables?: unknown) => {
      if (!error || isCancel(error)) return;

      const context: ApiErrorContext = { error, variables };

      if (resolveMetaValue(meta?.skip, context)) return;

      const message = resolveMetaValue(meta?.message, context) ?? DEFAULT_ERROR_TITLE;
      const description = resolveMetaValue(meta?.description, context) ?? extractDescription(error);
      const hideTraceIdAction = resolveMetaValue(meta?.hideTraceIdAction, context);
      const traceId = hideTraceIdAction ? undefined : extractTraceId(error);

      notification.error({
        message,
        description,
        pauseOnHover: true,
        showProgress: true,
        actions:
          traceId !== undefined ? (
            <div className="flex flex-row items-center gap-2">
              <div className="text-xs flex-1">Trace ID : {traceId}</div>
              <Button
                type="ghost"
                size="small"
                className="px-2!"
                onClick={async () => {
                  try {
                    if (!navigator?.clipboard?.writeText) {
                      throw new Error('Clipboard API tidak tersedia');
                    }
                    await navigator.clipboard.writeText(traceId);
                    showToast({
                      type: 'success',
                      message: 'Trace ID berhasil disalin ke clipboard',
                      placement: 'top',
                    });
                  } catch (clipboardError) {
                    console.error('Failed to copy trace id', clipboardError);
                    showToast({
                      type: 'error',
                      message: 'Gagal menyalin Trace ID',
                      placement: 'top',
                    });
                  }
                }}
              >
                <CopyIcon size={16} />
              </Button>
            </div>
          ) : undefined,
      });
    },
    [notification, showToast]
  );

  useEffect(() => {
    const unsubscribeMutations = queryClient.getMutationCache().subscribe(event => {
      if (event.type !== 'updated' || event.action.type !== 'error') return;

      const error = event.action.error;
      const variables = event.mutation.state.variables;
      const meta = event.mutation.options.meta as ApiErrorNotificationMeta | undefined;
      emitErrorNotification(error, meta, variables);
    });

    const unsubscribeQueries = queryClient.getQueryCache().subscribe(event => {
      if (event.type !== 'updated' || event.action.type !== 'error') return;

      const error = event.action.error;
      const meta = event.query.options.meta as ApiErrorNotificationMeta | undefined;
      emitErrorNotification(error, meta, event.query.queryKey);
    });

    return () => {
      unsubscribeMutations();
      unsubscribeQueries();
    };
  }, [emitErrorNotification, queryClient]);

  return (
    <>
      {toastHolder}
      {children}
    </>
  );
};

export default ApiErrorNotificationProvider;
